import { Draggable, Droppable } from "@hello-pangea/dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
	Box,
	Checkbox,
	Chip,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import type { MetadataApiProps } from "../../../../features/product/types/report-template";
import {
	blockFieldRows,
	type FieldRow,
	type TemplateBlock,
	type TemplateField,
	toggleField,
	updateField,
} from "../../../../features/product/utils/template-config";

const FORMAT_OPTIONS = [
	{ value: "text", label: "Texto" },
	{ value: "date", label: "Data" },
	{ value: "currency", label: "Moeda" },
	{ value: "number", label: "Número" },
];

type TemplateBlockFieldsProps = {
	block: TemplateBlock;
	blockIndex: number;
	apiName: string;
	metadata: MetadataApiProps[];
	onChange: (fields: TemplateField[]) => void;
};

function formatOptions(current: string) {
	if (!current || FORMAT_OPTIONS.some((option) => option.value === current)) {
		return FORMAT_OPTIONS;
	}
	return [...FORMAT_OPTIONS, { value: current, label: current }];
}

function IncludedFieldRow({
	row,
	index,
	blockIndex,
	onToggle,
	onUpdate,
}: {
	row: FieldRow;
	index: number;
	blockIndex: number;
	onToggle: () => void;
	onUpdate: (changes: Partial<TemplateField>) => void;
}) {
	return (
		<Draggable draggableId={`field-${blockIndex}-${row.key}`} index={index}>
			{(draggable, snapshot) => (
				<Box
					ref={draggable.innerRef}
					{...draggable.draggableProps}
					sx={{
						display: "flex",
						alignItems: "center",
						flexWrap: "wrap",
						gap: 1,
						py: 0.5,
						bgcolor: snapshot.isDragging ? "action.hover" : "transparent",
						borderRadius: 1,
					}}
				>
					<Box
						{...draggable.dragHandleProps}
						role="button"
						aria-label={`Arrastar campo ${row.key}`}
						sx={{ display: "flex", color: "text.secondary", cursor: "grab" }}
					>
						<DragIndicatorIcon fontSize="small" />
					</Box>
					<Checkbox
						size="small"
						checked
						onChange={onToggle}
						inputProps={{ "aria-label": `Incluir ${row.key}` }}
					/>
					<Box
						sx={{
							flex: { xs: "1 1 0", sm: "0 0 200px" },
							minWidth: 0,
						}}
					>
						<Typography
							variant="body2"
							sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
						>
							{row.key}
						</Typography>
						{row.hasTranslation && (
							<Chip size="small" variant="outlined" label="tradução no JSON" />
						)}
					</Box>
					<Box
						sx={{
							display: { xs: "block", sm: "none" },
							flexBasis: "100%",
							height: 0,
						}}
					/>
					<TextField
						size="small"
						value={row.label}
						onChange={(event) => onUpdate({ label: event.target.value })}
						inputProps={{ "aria-label": `Rótulo de ${row.key}` }}
						sx={{ flex: { xs: "1 1 0", sm: "1 1 200px" }, minWidth: 0 }}
					/>
					<Select
						size="small"
						value={row.format}
						onChange={(event) => onUpdate({ format: event.target.value })}
						SelectDisplayProps={
							{ "aria-label": `Formato de ${row.key}` } as object
						}
						sx={{ width: { xs: 110, sm: 130 } }}
					>
						{formatOptions(row.format).map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</Box>
			)}
		</Draggable>
	);
}

export default function TemplateBlockFields({
	block,
	blockIndex,
	apiName,
	metadata,
	onChange,
}: TemplateBlockFieldsProps) {
	const rows = blockFieldRows(block, metadata);
	const included = rows.filter((row) => row.included);
	const available = rows.filter((row) => !row.included);

	return (
		<Box sx={{ pt: 2, pl: { xs: 0, sm: 5 } }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					flexWrap: "wrap",
					gap: 1,
					mb: 1,
				}}
			>
				<Typography variant="subtitle2">
					{`Campos · API ${apiName} › ${block.source?.primary?.response_block ?? ""}`}
				</Typography>
				<Typography variant="body2" color="text.secondary">
					{`${included.length} de ${rows.length} incluídos`}
				</Typography>
			</Box>

			<Droppable
				droppableId={`fields-${blockIndex}`}
				type={`FIELD-${blockIndex}`}
			>
				{(droppable) => (
					<Box ref={droppable.innerRef} {...droppable.droppableProps}>
						{included.map((row, index) => (
							<IncludedFieldRow
								key={row.key}
								row={row}
								index={index}
								blockIndex={blockIndex}
								onToggle={() => onChange(toggleField(block, row.key, false))}
								onUpdate={(changes) =>
									onChange(updateField(block, row.key, changes))
								}
							/>
						))}
						{droppable.placeholder}
					</Box>
				)}
			</Droppable>

			{available.map((row) => (
				<Box
					key={row.key}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						py: 0.25,
					}}
				>
					<Box sx={{ width: 20, flexShrink: 0 }} />
					<Checkbox
						size="small"
						checked={false}
						onChange={() => onChange(toggleField(block, row.key, true))}
						inputProps={{ "aria-label": `Incluir ${row.key}` }}
					/>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{
							fontFamily: "monospace",
							overflowWrap: "anywhere",
							minWidth: 0,
						}}
					>
						{row.key}
					</Typography>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: { xs: "none", sm: "block" }, whiteSpace: "nowrap" }}
					>
						não incluído
					</Typography>
				</Box>
			))}
		</Box>
	);
}
